/* eslint-disable max-len */
import React, { useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Form, Field, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { AccountContext } from '../../components/AccountContext';
import { NotificationContext } from '../../components/GrowlNotifications';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { FormikCheckbox, InputTextWithValidationMessage } from '../../components/prime-formik';
import { parse } from 'query-string';
import {
  MAXIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER,
  MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER,
} from '../../shared/Constants';

const messages = defineMessages({
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  respondentDeletionRules: {
    id: 'menu.respondent-deletion-rules',
    defaultMessage: 'Respondent deletion rules',
  },
});

const UPDATE_RESPONDENT_DELETION_RULES = gql`
  mutation updateRespondentDeletionRules($input: UpdateRespondentDeletionRulesInput!) {
    updateRespondentDeletionRules(input: $input) {
      account {
        id
        respondentData {
          internalRespondents {
            includedInPlan
            presentlyUsed
            deletionRule {
              type
              numberOfDaysToAnonymizeAfter
            }
          }
          externalRespondents {
            includedInPlan
            presentlyUsed
            deletionRule {
              type
              numberOfDaysToAnonymizeAfter
            }
          }
        }
      }
    }
  }
`;

const UpdateRespondentDeletionRules = ({ account, toggleEdit }) => {
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const location = useLocation();

  const { formatMessage } = useIntl();

  const params = parse(location.search);
  const editRespondentDeletionRulesFlag = params.editRespondentDeletionRules || false;
  const focusInternalRule = params.focusInternalRule || false;
  const focusExternalRule = params.focusExternalRule || false;

  const [updateRespondentDeletionRulesMutation] = useMutation(UPDATE_RESPONDENT_DELETION_RULES, {
    refetchQueries: ['querySingleAccountById', 'getViewerAccounts'],
  });

  const {
    respondentData: { internalRespondents, externalRespondents },
  } = account;

  const manualOnlyDeletionRule = internalRespondents.deletionRule.type === 'MANUAL';
  const numberOfDaysToAnonymizeInternalRespondentsAfter = manualOnlyDeletionRule
    ? MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER
    : internalRespondents.deletionRule.numberOfDaysToAnonymizeAfter;
  const numberOfDaysToAnonymizeExternalRespondentsAfter = externalRespondents.deletionRule.numberOfDaysToAnonymizeAfter;

  const initialValues = {
    account: currentAccount,
    manualOnly: manualOnlyDeletionRule,
    numberOfDaysToAnonymizeInternalRespondentsAfter,
    numberOfDaysToAnonymizeExternalRespondentsAfter,
  };

  const validationSchema = Yup.object().shape({
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    manualOnly: Yup.boolean().required(formatMessage(validationMessages.required)),
    numberOfDaysToAnonymizeInternalRespondentsAfter: Yup.number().when('manualOnly', {
      is: (manualOnly) => !manualOnly,
      then: Yup.number()
        .required(formatMessage(validationMessages.required))
        .min(MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER, formatMessage(validationMessages.required))
        .max(MAXIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER, formatMessage(validationMessages.required)),
      otherwise: Yup.number(),
    }),
    numberOfDaysToAnonymizeExternalRespondentsAfter: Yup.number()
      .required(formatMessage(validationMessages.required))
      .min(MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER, formatMessage(validationMessages.required))
      .max(MAXIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER, formatMessage(validationMessages.required)),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const internalRespondentRuleType = values.manualOnly ? 'MANUAL' : 'AFTER_NUMBER_OF_DAYS';
    const externalRespondentRuleType = 'AFTER_NUMBER_OF_DAYS';

    try {
      await updateRespondentDeletionRulesMutation({
        variables: {
          input: {
            account: currentAccount,
            internalRespondentDeletionRuleType: internalRespondentRuleType,
            anonymizeInternalRespondentsAfterDays: values.numberOfDaysToAnonymizeInternalRespondentsAfter,
            externalRespondentDeletionRuleType: externalRespondentRuleType,
            anonymizeExternalRespondentsAfterDays: values.numberOfDaysToAnonymizeExternalRespondentsAfter,
          },
        },
      });

      setSubmitting(false);
      growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
      toggleEdit();
    } catch (error) {
      growl.show({ severity: 'error', detail: error.message, sticky: true });
    }
  };

  useEffect(() => {
    if (focusInternalRule) {
      document.querySelector(`#numberOfDaysToAnonymizeInternalRespondentsAfter`).focus();
    }

    if (focusExternalRule) {
      document.querySelector(`#numberOfDaysToAnonymizeExternalRespondentsAfter`).focus();
    }
  }, [focusInternalRule, focusExternalRule]);

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel
          header={
            <div className={editRespondentDeletionRulesFlag ? 'blinking-text' : ''}>
              {formatMessage(messages.respondentDeletionRules)}
            </div>
          }
        >
          <Formik onSubmit={handleSubmit} initialValues={initialValues} u validationSchema={validationSchema}>
            {({ setFieldValue, values: { manualOnly } }) => (
              <div className="respondent-delete-rules-form">
                <Form>
                  <div className="internal-section">
                    <div>
                      <FormattedMessage
                        id="dashboard.internal-respondents.title"
                        defaultMessage="Internal Respondents (employees)"
                        tagName="strong"
                      />
                    </div>
                    <div>
                      <Field
                        id="numberOfDaysToAnonymizeInternalRespondentsAfter"
                        name="numberOfDaysToAnonymizeInternalRespondentsAfter"
                        type="number"
                        min={MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER}
                        labelText={`${MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER}-${MAXIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER} days after completion`}
                        disabled={manualOnly}
                        component={InputTextWithValidationMessage}
                      />
                    </div>
                    <div>
                      <Field
                        id="manualOnly"
                        name="manualOnly"
                        labelText="Manual only"
                        component={FormikCheckbox}
                        setFieldValue={setFieldValue}
                      />
                    </div>
                  </div>
                  <div className="external-section">
                    <div>
                      <FormattedMessage
                        id="dashboard.external-respondents.title"
                        defaultMessage="External Respondents (candidates)"
                        tagName="strong"
                      />
                    </div>
                    <div>
                      <Field
                        id="numberOfDaysToAnonymizeExternalRespondentsAfter"
                        name="numberOfDaysToAnonymizeExternalRespondentsAfter"
                        type="number"
                        min={MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER}
                        labelText={`${MINIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER}-${MAXIMUM_NUMBER_OF_DAYS_TO_ANONYMIZE_RESPONDENTS_AFTER} days after completion`}
                        component={InputTextWithValidationMessage}
                      />
                    </div>
                  </div>
                  <div className="buttons">
                    <div>
                      <Button
                        type="button"
                        className="p-button-secondary"
                        label={formatMessage(messages.cancel)}
                        onClick={toggleEdit}
                      />
                    </div>
                    <div>
                      <Button type="submit" label={formatMessage(messages.submit)} />
                    </div>
                  </div>
                  <Debug />
                </Form>
              </div>
            )}
          </Formik>
        </Panel>
      </div>
    </div>
  );
};

export default UpdateRespondentDeletionRules;
